import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import { NovaSectionLoader, NovaButtonLoader } from './ui/NovaLoader';
import '../CSS/Facture.css';

const emptyForm = {
  id_facture: null,
  numero_facture: '',
  nom_client: '',
  collaborateur: '',
  date_facture: '',
  date_echeance: '',
  montant_ht: '',
  montant_ttc: '',
  devise: 'EUR',
  status: 'Non payée',
  file: null
};

const FacturePage = () => {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [filterStatus, setFilterStatus] = useState('Toutes');
  const [filterClient, setFilterClient] = useState('Tous');
  const [filterDevise, setFilterDevise] = useState('Toutes');
  const [filterEcheance, setFilterEcheance] = useState('Toutes');
  const [filterSearch, setFilterSearch] = useState('');
  const [tablePage, setTablePage] = useState(0);
  const rowsPerPage = 50;

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [factureToDelete, setFactureToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [unpayDialogOpen, setUnpayDialogOpen] = useState(false);
  const [factureToToggle, setFactureToToggle] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  const normalizeFull = (value) => {
    if (!value) return '';

    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .sort()
      .join(' ');
  };

  const parseFrenchDateToInput = (value) => {
    if (!value) return '';

    const text = String(value).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return '';

    const day = String(match[1]).padStart(2, '0');
    const month = String(match[2]).padStart(2, '0');
    const year = match[3];

    return `${year}-${month}-${day}`;
  };

  const parseFactureDateForDelay = (value) => {
    const inputValue = parseFrenchDateToInput(value);

    if (!inputValue) return null;

    const date = new Date(`${inputValue}T00:00:00`);

    if (Number.isNaN(date.getTime())) return null;

    return date;
  };

  const isFactureOverdue = (facture) => {
    if (!facture || facture.status === 'Payée') return false;

    const dueDate = parseFactureDateForDelay(facture.date_echeance);

    if (!dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const getDevise = (facture) => String(facture?.devise || 'EUR').toUpperCase();

  const formatAmount = (value, devise = 'EUR') => {
    const num = Number(String(value ?? '0').replace(',', '.'));

    if (!Number.isFinite(num)) return '—';

    if (String(devise).toUpperCase() === 'MAD') {
      return `${num.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} DH`;
    }

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const fetchFactures = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await axios.get(`${apiUrl}/facturebiam`, getAuthHeaders());
      setFactures(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement factures:', error);

      if (!silent) {
        setNotification({
          open: true,
          message: 'Erreur lors du chargement des factures.',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchFactures();
  }, [fetchFactures]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!formDialogOpen && !deleteDialogOpen && !unpayDialogOpen && !saving && !isDeleting && !isUpdatingStatus) {
        fetchFactures({ silent: true });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [formDialogOpen, deleteDialogOpen, unpayDialogOpen, saving, isDeleting, isUpdatingStatus, fetchFactures]);

  useEffect(() => {
    setTablePage(0);
  }, [filterStatus, filterClient, filterDevise, filterEcheance, filterSearch]);

  const clientNames = useMemo(() => {
    const uniqueClientsMap = new Map();

    factures.forEach((facture) => {
      const original = facture.nom_client;
      const normalized = normalizeFull(original);

      if (normalized && !uniqueClientsMap.has(normalized)) {
        uniqueClientsMap.set(normalized, original?.trim() || '');
      }
    });

    return Array.from(uniqueClientsMap.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], 'fr', { sensitivity: 'base' })
    );
  }, [factures]);

  const filteredFactures = useMemo(() => {
    let filtered = factures;

    if (filterStatus !== 'Toutes') {
      filtered = filtered.filter((facture) => facture.status === filterStatus);
    }

    if (filterClient !== 'Tous') {
      filtered = filtered.filter((facture) => normalizeFull(facture.nom_client) === filterClient);
    }

    if (filterDevise !== 'Toutes') {
      filtered = filtered.filter((facture) => getDevise(facture) === filterDevise);
    }

    if (filterEcheance === 'En retard') {
      filtered = filtered.filter((facture) => isFactureOverdue(facture));
    }

    if (filterEcheance === 'Non échues') {
      filtered = filtered.filter((facture) => !isFactureOverdue(facture));
    }

    const search = filterSearch.trim().toLowerCase();

    if (search) {
      filtered = filtered.filter((facture) => {
        return [
          facture.numero_facture,
          facture.nom_client,
          facture.collaborateur,
          facture.date_facture,
          facture.date_echeance,
          facture.status,
          facture.devise
        ].some((value) => String(value || '').toLowerCase().includes(search));
      });
    }

    return filtered;
  }, [factures, filterStatus, filterClient, filterDevise, filterEcheance, filterSearch]);

  const stats = useMemo(() => {
    const total = factures.length;
    const payees = factures.filter((facture) => facture.status === 'Payée').length;
    const nonPayees = factures.filter((facture) => facture.status !== 'Payée').length;
    const overdue = factures.filter((facture) => isFactureOverdue(facture)).length;

    const facturesEUR = factures.filter((facture) => getDevise(facture) === 'EUR');
    const facturesMAD = factures.filter((facture) => getDevise(facture) === 'MAD');

    const countEUR = facturesEUR.length;
    const countMAD = facturesMAD.length;

    const totalEUR = facturesEUR.reduce((sum, facture) => {
      return sum + Number(String(facture.montant_ttc || '0').replace(',', '.'));
    }, 0);

    const totalMAD = facturesMAD.reduce((sum, facture) => {
      return sum + Number(String(facture.montant_ttc || '0').replace(',', '.'));
    }, 0);

    return { total, payees, nonPayees, overdue, countEUR, countMAD, totalEUR, totalMAD };
  }, [factures]);

  const paginatedFactures = useMemo(() => {
    const start = tablePage * rowsPerPage;
    return filteredFactures.slice(start, start + rowsPerPage);
  }, [filteredFactures, tablePage, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredFactures.length / rowsPerPage) - 1);

    if (tablePage > maxPage) {
      setTablePage(maxPage);
    }
  }, [filteredFactures.length, tablePage, rowsPerPage]);

  const resetFilters = () => {
    setFilterStatus('Toutes');
    setFilterClient('Tous');
    setFilterDevise('Toutes');
    setFilterEcheance('Toutes');
    setFilterSearch('');
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openAddDialog = () => {
    setFormMode('add');
    resetForm();
    setFormDialogOpen(true);
  };

  const openEditDialog = (facture) => {
    setFormMode('edit');

    setForm({
      id_facture: facture.id_facture,
      numero_facture: facture.numero_facture || '',
      nom_client: facture.nom_client || '',
      collaborateur: facture.collaborateur === '—' ? '' : facture.collaborateur || '',
      date_facture: parseFrenchDateToInput(facture.date_facture),
      date_echeance: parseFrenchDateToInput(facture.date_echeance),
      montant_ht: facture.montant_ht ?? '',
      montant_ttc: facture.montant_ttc ?? '',
      devise: getDevise(facture),
      status: facture.status || 'Non payée',
      file: null
    });

    setFormDialogOpen(true);
  };

  const validateForm = () => {
    if (
      !form.nom_client ||
      !form.collaborateur ||
      !form.date_facture ||
      !form.date_echeance ||
      !form.montant_ht ||
      !form.montant_ttc ||
      !form.devise
    ) {
      setNotification({
        open: true,
        message: 'Veuillez remplir tous les champs obligatoires.',
        severity: 'error'
      });
      return false;
    }

    if (formMode === 'add' && !form.file) {
      setNotification({
        open: true,
        message: 'Veuillez ajouter le PDF de la facture.',
        severity: 'error'
      });
      return false;
    }

    return true;
  };

  const handleSaveFacture = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const formData = new FormData();

      if (form.file) {
        formData.append('facture_pdf', form.file);
      }

      formData.append('date_facture', form.date_facture);
      formData.append('date_echeance', form.date_echeance);
      formData.append('status', form.status);
      formData.append('nom_client', form.nom_client);
      formData.append('collaborateur', form.collaborateur);
      formData.append('montant_ht', form.montant_ht);
      formData.append('montant_ttc', form.montant_ttc);
      formData.append('numero_facture', form.numero_facture);
      formData.append('devise', form.devise);

      if (formMode === 'edit') {
        await axios.put(`${apiUrl}/facturebiam/${form.id_facture}`, formData, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setNotification({
          open: true,
          message: 'Facture modifiée avec succès.',
          severity: 'success'
        });
      } else {
        await axios.post(`${apiUrl}/facturebiam/ajouter`, formData, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setNotification({
          open: true,
          message: 'Facture ajoutée avec succès.',
          severity: 'success'
        });
      }

      setFormDialogOpen(false);
      resetForm();

      await fetchFactures({ silent: true });
    } catch (error) {
      console.error('Erreur sauvegarde facture:', error.response?.data || error);

      setNotification({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'enregistrement de la facture.",
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (factureId) => {
    try {
      const response = await axios.get(`${apiUrl}/facturebiam/${factureId}/telecharger`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = urlBlob;
      link.setAttribute('download', `facture_${factureId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Erreur téléchargement facture:', error);
      setNotification({
        open: true,
        message: 'Erreur lors du téléchargement de la facture.',
        severity: 'error'
      });
    }
  };

  const updateStatus = async (factureId, newStatus) => {
    if (!factureId) return;

    setIsUpdatingStatus(true);

    try {
      await axios.put(
        `${apiUrl}/facturebiam/status/${factureId}`,
        { status: newStatus },
        getAuthHeaders()
      );

      setNotification({
        open: true,
        message: newStatus === 'Payée' ? 'Facture marquée comme payée.' : 'Facture remise en non payée.',
        severity: 'success'
      });

      await fetchFactures({ silent: true });
    } catch (error) {
      console.error('Erreur statut facture:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de la mise à jour de la facture.',
        severity: 'error'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteFacture = async () => {
    if (!factureToDelete) return;

    setIsDeleting(true);

    try {
      await axios.delete(`${apiUrl}/facturebiam/${factureToDelete.id_facture}`, getAuthHeaders());

      setNotification({
        open: true,
        message: 'Facture supprimée avec succès.',
        severity: 'success'
      });

      setDeleteDialogOpen(false);
      setFactureToDelete(null);

      await fetchFactures({ silent: true });
    } catch (error) {
      console.error('Erreur suppression facture:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de la suppression de la facture.',
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <LayoutDashboard>
      <main className="factures-page">
        <section className="factures-hero">
          <div className="factures-hero-text">
            <span className="factures-pill">Gestion financière</span>
            <h1>Factures</h1>
            <p>
              Ajoutez, modifiez, téléchargez et suivez les factures avec les montants en euros ou en dirhams marocains.
            </p>
          </div>

          <Stack className="factures-hero-actions" direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              className="factures-refresh-btn"
              variant="outlined"
              startIcon={refreshing ? null : <RefreshRoundedIcon />}
              disabled={refreshing}
              onClick={() => fetchFactures({ silent: true })}
            >
              {refreshing ? 'Actualisation...' : 'Rafraîchir'}
            </Button>

            <Button
              className="factures-add-btn"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openAddDialog}
            >
              Ajouter une facture
            </Button>
          </Stack>
        </section>

        <Container maxWidth={false} disableGutters className="factures-container-modern">
          <section className="factures-stats-grid">
            <article className="factures-stat-card">
              <span>Total</span>
              <strong>{stats.total}</strong>
              <small>facture(s)</small>
            </article>

            <article className="factures-stat-card">
              <span>Payées</span>
              <strong>{stats.payees}</strong>
              <small>facture(s)</small>
            </article>

            <article className="factures-stat-card">
              <span>Non payées</span>
              <strong>{stats.nonPayees}</strong>
              <small>à suivre</small>
            </article>

            <article className="factures-stat-card facture-stat-overdue">
              <span>En retard</span>
              <strong>{stats.overdue}</strong>
              <small>échéance dépassée</small>
            </article>

            <article className="factures-stat-card">
              <span>Factures €</span>
              <strong>{stats.countEUR}</strong>
              <small>{formatAmount(stats.totalEUR, 'EUR')} TTC</small>
            </article>

            <article className="factures-stat-card">
              <span>Factures DH</span>
              <strong>{stats.countMAD}</strong>
              <small>{formatAmount(stats.totalMAD, 'MAD')} TTC</small>
            </article>
          </section>

          <section className="factures-toolbar-card">
            <div className="factures-toolbar-title">
              <Typography className="factures-section-title">Liste des factures</Typography>
              <Typography className="factures-section-helper">
                {filteredFactures.length} facture(s) affichée(s) sur {factures.length}
              </Typography>
            </div>

            <div className="factures-filters">
              <FormControl size="small">
                <TextField
                  select
                  label="Statut"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="Toutes">Toutes</MenuItem>
                  <MenuItem value="Payée">Payée</MenuItem>
                  <MenuItem value="Non payée">Non payée</MenuItem>
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label="Client"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                >
                  <MenuItem value="Tous">Tous</MenuItem>
                  {clientNames.map(([key, original]) => (
                    <MenuItem key={key} value={key}>{original}</MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label="Devise"
                  value={filterDevise}
                  onChange={(e) => setFilterDevise(e.target.value)}
                >
                  <MenuItem value="Toutes">Toutes</MenuItem>
                  <MenuItem value="EUR">Euro (€)</MenuItem>
                  <MenuItem value="MAD">Dirham marocain (DH)</MenuItem>
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label="Échéance"
                  value={filterEcheance}
                  onChange={(e) => setFilterEcheance(e.target.value)}
                >
                  <MenuItem value="Toutes">Toutes</MenuItem>
                  <MenuItem value="En retard">En retard</MenuItem>
                  <MenuItem value="Non échues">Non échues</MenuItem>
                </TextField>
              </FormControl>

              <TextField
                size="small"
                label="Rechercher facture"
                placeholder="N°, client, collaborateur..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />

              <Button className="factures-reset-btn" variant="outlined" onClick={resetFilters}>
                Réinitialiser
              </Button>
            </div>
          </section>

          <section className="factures-table-card">
            {loading ? (
              <NovaSectionLoader label="Chargement des factures" helper="Merci de patienter..." />
            ) : (
              <TableContainer className="factures-table-container">
                <Table className="factures-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>N° facture</TableCell>
                      <TableCell>Date facture</TableCell>
                      <TableCell>Échéance</TableCell>
                      <TableCell>Collaborateur</TableCell>
                      <TableCell align="right">HT</TableCell>
                      <TableCell align="right">TTC</TableCell>
                      <TableCell align="center">Devise</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedFactures.map((facture) => {
                      const overdue = isFactureOverdue(facture);
                      const devise = getDevise(facture);

                      return (
                        <TableRow key={facture.id_facture}>
                          <TableCell>
                            <div className="facture-client-cell">
                              <ReceiptLongRoundedIcon />
                              <span>{facture.nom_client || 'Sans client'}</span>
                            </div>
                          </TableCell>

                          <TableCell>{facture.numero_facture || '—'}</TableCell>
                          <TableCell>{facture.date_facture || '—'}</TableCell>
                          <TableCell>{facture.date_echeance || '—'}</TableCell>
                          <TableCell>{facture.collaborateur || '—'}</TableCell>

                          <TableCell align="right">{formatAmount(facture.montant_ht, devise)}</TableCell>

                          <TableCell align="right">
                            <strong>{formatAmount(facture.montant_ttc, devise)}</strong>
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={devise === 'MAD' ? 'DH' : '€'}
                              className={devise === 'MAD' ? 'facture-devise-chip mad' : 'facture-devise-chip eur'}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              label={overdue ? 'En retard' : facture.status || 'Inconnu'}
                              size="small"
                              className={
                                facture.status === 'Payée'
                                  ? 'facture-status-chip paid'
                                  : overdue
                                    ? 'facture-status-chip overdue'
                                    : 'facture-status-chip unpaid'
                              }
                            />
                          </TableCell>

                          <TableCell align="center">
                            <div className="facture-actions-cell">
                              <Button
                                className="facture-mini-btn download"
                                onClick={() => handleDownload(facture.id_facture)}
                              >
                                <DownloadRoundedIcon />
                              </Button>

                              <Button
                                className="facture-mini-btn edit"
                                onClick={() => openEditDialog(facture)}
                              >
                                <EditRoundedIcon />
                              </Button>

                              <Button
                                className={facture.status === 'Payée' ? 'facture-mini-btn pay-action is-paid' : 'facture-mini-btn pay-action is-unpaid'}
                                disabled={isUpdatingStatus}
                                onClick={() => {
                                  if (facture.status === 'Payée') {
                                    setFactureToToggle(facture);
                                    setUnpayDialogOpen(true);
                                  } else {
                                    updateStatus(facture.id_facture, 'Payée');
                                  }
                                }}
                              >
                                <CheckCircleRoundedIcon />
                              </Button>

                              <Button
                                className="facture-mini-btn delete"
                                onClick={() => {
                                  setFactureToDelete(facture);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <DeleteRoundedIcon />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {filteredFactures.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <div className="factures-empty-state">
                            Aucune facture disponible pour ces filtres.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <TablePagination
                  component="div"
                  count={filteredFactures.length}
                  page={tablePage}
                  onPageChange={(event, newPage) => setTablePage(newPage)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[50]}
                  labelRowsPerPage="Lignes par page"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                  }
                  className="factures-pagination"
                />
              </TableContainer>
            )}
          </section>
        </Container>

        <Dialog
          open={formDialogOpen}
          onClose={() => !saving && setFormDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {formMode === 'edit' ? 'Modifier la facture' : 'Ajouter une facture'}
          </DialogTitle>

          <DialogContent>
            <Box className="facture-form-grid">
              <TextField
                label="Numéro de facture"
                value={form.numero_facture}
                onChange={(e) => updateForm('numero_facture', e.target.value)}
                fullWidth
              />

              <TextField
                label="Nom du client"
                value={form.nom_client}
                onChange={(e) => updateForm('nom_client', e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Collaborateur"
                value={form.collaborateur}
                onChange={(e) => updateForm('collaborateur', e.target.value)}
                fullWidth
                required
              />

              <TextField
                select
                label="Devise"
                value={form.devise}
                onChange={(e) => updateForm('devise', e.target.value)}
                fullWidth
                required
              >
                <MenuItem value="EUR">Euro (€)</MenuItem>
                <MenuItem value="MAD">Dirham marocain (DH)</MenuItem>
              </TextField>

              <TextField
                label={`Montant HT ${form.devise === 'MAD' ? '(DH)' : '(€)'}`}
                type="number"
                value={form.montant_ht}
                onChange={(e) => updateForm('montant_ht', e.target.value)}
                fullWidth
                required
              />

              <TextField
                label={`Montant TTC ${form.devise === 'MAD' ? '(DH)' : '(€)'}`}
                type="number"
                value={form.montant_ttc}
                onChange={(e) => updateForm('montant_ttc', e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Date facture"
                type="date"
                value={form.date_facture}
                onChange={(e) => updateForm('date_facture', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Date échéance"
                type="date"
                value={form.date_echeance}
                onChange={(e) => updateForm('date_echeance', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                label="Statut"
                value={form.status}
                onChange={(e) => updateForm('status', e.target.value)}
                fullWidth
              >
                <MenuItem value="Non payée">Non payée</MenuItem>
                <MenuItem value="Payée">Payée</MenuItem>
              </TextField>

              <Button variant="outlined" component="label" className="facture-file-picker">
                {formMode === 'edit' ? 'Changer le PDF' : 'Choisir le PDF'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf,.pdf"
                  onChange={(e) => updateForm('file', e.target.files?.[0] || null)}
                />
              </Button>
            </Box>

            {form.file && (
              <Typography className="facture-selected-file">
                Fichier sélectionné : {form.file.name}
              </Typography>
            )}

            {formMode === 'edit' && !form.file && (
              <Typography className="facture-edit-helper">
                Vous pouvez modifier toutes les informations sans changer le PDF.
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={() => setFormDialogOpen(false)} disabled={saving}>
              Annuler
            </Button>

            <Button variant="contained" onClick={handleSaveFacture} disabled={saving}>
              {saving ? <NovaButtonLoader label="Enregistrement" /> : formMode === 'edit' ? 'Valider modification' : 'Ajouter la facture'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
          <DialogTitle>Supprimer la facture</DialogTitle>
          <DialogContent>
            <Typography>
              {isDeleting ? 'Suppression en cours...' : 'Voulez-vous vraiment supprimer cette facture ?'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Annuler
            </Button>
            <Button color="error" onClick={handleDeleteFacture} disabled={isDeleting}>
              {isDeleting ? <NovaButtonLoader label="Suppression" /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={unpayDialogOpen} onClose={() => !isUpdatingStatus && setUnpayDialogOpen(false)}>
          <DialogTitle>Confirmer</DialogTitle>
          <DialogContent>
            <Typography>Remettre cette facture en “Non payée” ?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnpayDialogOpen(false)} disabled={isUpdatingStatus}>
              Annuler
            </Button>
            <Button
              color="error"
              disabled={isUpdatingStatus}
              onClick={async () => {
                if (factureToToggle) {
                  await updateStatus(factureToToggle.id_facture, 'Non payée');
                  setUnpayDialogOpen(false);
                  setFactureToToggle(null);
                }
              }}
            >
              {isUpdatingStatus ? <NovaButtonLoader label="Mise à jour" /> : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={2800}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={notification.severity} className="factures-snackbar-alert">
            {notification.message}
          </Alert>
        </Snackbar>
      </main>
    </LayoutDashboard>
  );
};

export default FacturePage;
